import React from "react";

import { AppRoutes } from "./AppRoutes";
import { AuthenticationApp } from "./AuthenticationApp";
import { useGetMaintenanceQuery } from "./app/api/public/app";
import { useAutoSignInQuery } from "./app/api/public/auth";
import { useAppSelector } from "./app/store";
import { selectCurrentUser } from "./app/store/auth";
import { ScrollRestorer } from "./features/core/ScrollRestorer";
import { Theme } from "./features/core/Theme";
import { Tracking } from "./features/core/Tracking";
import { InformationNoticeDialog } from "./features/informationNotice/InformationNoticeDialog";
import { SessionDialog } from "./features/session/SessionDialog";
import { SessionWatcher } from "./features/session/SessionWatcher";
import { Error500Page } from "./pages/Error500Page";
import { MaintenancePage } from "./pages/MaintenancePage";
import { FaqPage } from "./pages/FaqPage";

const MainApp = React.lazy(() =>
  import(/* webpackPrefetch: true */ "./MainApp").then((module) => ({
    default: module.MainApp,
  })),
);

export const App = () => {
  const { isError, isLoading, isSuccess } = useAutoSignInQuery();
  const [hash, setHash] = React.useState(window.location.hash.slice(1));

  React.useEffect(() => {
    if (!isLoading) {
      document.getElementById("splash-screen")?.remove();
    }
  }, [isLoading]);

  React.useEffect(() => {
    const handleHashChange = () => {
      setHash(window.location.hash.slice(1));
    };

    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [hash]);

  const { currentData: maintenance } = useGetMaintenanceQuery(undefined, {
    pollingInterval: 15 * 60 * 1000,
    skip: !isSuccess,
  });

  return (
    <>
      {(() => {
        if (hash === "faq") {
          return <FaqPage />;
        }

        if (maintenance?.enabled) {
          return <MaintenancePage />;
        }

        if (isError) {
          return <Error500Page />;
        }

        if (isSuccess) {
          return (
            <>
              <AppLayout />
              <AppRoutes />
              <ScrollRestorer />
              <Theme />
              <Tracking />
              <InformationNoticeDialog />
              <SessionDialog />
            </>
          );
        }

        return null;
      })()}
      <SessionWatcher />
    </>
  );
};

const AppLayout = () => {
  const currentUser = useAppSelector(selectCurrentUser);

  return currentUser?.hasAcceptedTermsOfService ? (
    <React.Suspense fallback={null}>
      <MainApp />
    </React.Suspense>
  ) : (
    <AuthenticationApp />
  );
};
